import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  IdentityEditorConfig,
  IterablesEditorConfig,
} from '../core/models/editorContract.model';
import { UtilsService } from '../core/services/utils.service';
import { ResourceService } from '../core/services/resource.service';
import { EMPTY, of, Subscription } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ClipboardService, IClipboardResponse } from 'ngx-clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TransService } from '../core/models/translation.model';
import { MatDialogRef } from '@angular/material/dialog';
import { ModalComponent } from '../core/components/modal/modal.component';
import {
  BasicResource,
  FunctionExpressionDef,
  PopupConfig,
  Resource,
} from '../core/models/dataContract.model';
import { ModalService } from '../core/services/modal.service';
import { ModalType } from '../core/models/componentContract.model';
import { WindowCloseResult } from '@progress/kendo-angular-dialog';
import { EditorIterablesComponent } from '../core/components/editor-iterables/editor-iterables.component';
import { EditorIdentityComponent } from '../core/components/editor-identity/editor-identity.component';
import { WizardService } from '../core/services/wizard.service';
import { NgForm } from '@angular/forms';
import { ConfigService } from '../core/services/config.service';

@Component({
  selector: 'app-function-expression',
  templateUrl: './function-expression.component.html',
  styleUrls: ['./function-expression.component.scss'],
  standalone: false
})
export class FunctionExpressionComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  @ViewChild('idpRequestor') idpRequestor: EditorIdentityComponent;
  @ViewChild('idpTarget') idpTarget: EditorIdentityComponent;
  @ViewChild('idpTemplate') idpTemplate: EditorIdentityComponent;
  @ViewChild('itrWorkflowData') itrWorkflowData: EditorIterablesComponent;
  @ViewChild('itrDelta') itrDelta: EditorIterablesComponent;
  @ViewChild('itrDeltaBeforeChange')
  itrDeltaBeforeChange: EditorIterablesComponent;
  @ViewChild('itrTemplateParameter')
  itrTemplateParameter: EditorIterablesComponent;

  @ViewChild('templateParamsForm') templateParamsForm: NgForm;

  expression: string;
  expressionResult: any;
  expressionResultRaw = '';
  expressionResultString = '';

  resultMode = 'json';

  isLoading = false;

  targetResource = {
    dataType: 'Reference',
    displayName: 'key_targetResource',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'TargetResource',
    value: null,
  };

  requestorResource = {
    dataType: 'Reference',
    displayName: 'key_requestor',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'RequestorResource',
    value: null,
  };

  templateResource = {
    dataType: 'Reference',
    displayName: 'l10n_fxTemplate',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'TemplateResource',
    value: null,
  };

  workflowDataExpression = {
    dataType: 'Dictionary',
    displayName: 'key_workflowDataExpressions',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'attrWorkflowData',
    value: null,
  };

  deltaExpression = {
    dataType: 'Dictionary',
    displayName: 'l10n_delta',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'attrDelta',
    value: null,
  };

  deltaBeforeChangeDataExpression = {
    dataType: 'Dictionary',
    displayName: 'l10n_deltaBeforeChange',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'attrDeltaBeforeChange',
    value: null,
  };

  templateParameter = {
    dataType: 'Dictionary',
    displayName: 'key_inputParameters',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'attrTempateParameter',
    value: null,
  };

  idpTemplateConfig: IdentityEditorConfig;

  configWorkflowDataExpression = new IterablesEditorConfig();
  configDeltaExpression = new IterablesEditorConfig();

  configTemplateParameter = new IterablesEditorConfig();

  customViewSetting: any;
  expressions: Array<{
    key: string;
    value: {
      expression: string;
      expressionDef: FunctionExpressionDef;
    };
  }> = [];
  savedExpressions: {
    [id: string]: {
      expression: string;
      expressionDef: FunctionExpressionDef;
    };
  } = {};
  selectedExpression: {
    key: string;
    value: {
      expression: string;
      expressionDef: FunctionExpressionDef;
    };
  };

  resultCode: string;
  resultState = '';

  templateId = '';
  parameterConfig: Array<any> = [];

  private customFunctionExpressionKey = 'savedExpressions';

  private buildExpressionDef(): FunctionExpressionDef {
    const expressionDef: FunctionExpressionDef = {
      expression: this.expression,
    };

    const requestor = this.utils.ExtraValue(
      this.requestorResource,
      'value:ObjectID'
    );
    if (this.templateId) {
      if (this.parameterConfig && this.parameterConfig.length > 0) {
        expressionDef.variableContext = this.buildVariableContext();
      }
    } else {
      const templateParams = this.utils.ExtraValue(
        this.templateParameter,
        'value'
      );
      if (templateParams) {
        expressionDef.variableContext = this.buildVariableContext(
          Object.keys(templateParams).map((key) => {
            return { ...templateParams[key], paramKey: key };
          })
        );
      }
    }
    if (requestor) {
      expressionDef.requestor = requestor;
    }
    const target = this.utils.ExtraValue(this.targetResource, 'value:ObjectID');
    if (target) {
      expressionDef.target = target;
    }
    const wfData = this.utils.ExtraValue(this.workflowDataExpression, 'value');
    if (wfData) {
      expressionDef.workflowDataExpressions = wfData;
    }
    const delta = this.utils.ExtraValue(this.deltaExpression, 'value');
    if (delta) {
      expressionDef.delta = delta;
    }
    const deltaBeforeChange = this.utils.ExtraValue(
      this.deltaBeforeChangeDataExpression,
      'value'
    );
    if (deltaBeforeChange) {
      expressionDef.deltaBeforeChange = deltaBeforeChange;
    }

    return expressionDef;
  }

  private buildParameterConfig(conf: any) {
    if (conf) {
      for (const [key, value] of Object.entries(conf)) {
        value['paramKey'] = key;
        value['paramValue'] = null;

        this.parameterConfig.push(value);
      }
    }
  }

  private buildVariableContext(context?: Array<any>): any {
    const result = {};
    const conf = context ?? this.parameterConfig;
    if (conf && conf.length > 0) {
      conf.forEach((param) => {
        // use default value
        let value =
          param.paramValue === null ||
            param.paramValue === undefined ||
            param.paramValue === ''
            ? param.testvalue
            : param.paramValue;
        if (value !== null && value !== undefined) {
          switch (param.datatype) {
            case 'integer':
              value = Number(value);
              break;
            case 'boolean':
              value = /^true$/i.test(value);
              break;
            case 'object':
              value = JSON.parse(value);
              break;
            default:
              break;
          }
        } else {
          value = null;
        }
        result[param.paramKey] = value;
      });
    }
    return result;
  }

  private getTemplateInfo(id: string) {
    this.subscription.add(
      this.resource
        .getResourceByID(id, [
          'displayname',
          'inputparameters',
          'functionexpression',
        ])
        .pipe(
          tap((result: Resource) => {
            this.expression = this.utils.ExtraValue(
              result,
              'functionexpression'
            );
            this.buildParameterConfig(
              this.utils.ExtraValue(result, 'inputparameters')
            );
          })
        )
        .subscribe()
    );
  }

  constructor(
    private utils: UtilsService,
    private resource: ResourceService,
    private clipboard: ClipboardService,
    private snackbar: MatSnackBar,
    private translate: TransService,
    private modal: ModalService,
    private wizard: WizardService,
    private config: ConfigService
  ) { }

  ngOnInit(): void {
    this.customViewSetting = this.resource.customViewSetting;
    if (
      this.customViewSetting.hasOwnProperty(this.customFunctionExpressionKey)
    ) {
      this.savedExpressions =
        this.customViewSetting[this.customFunctionExpressionKey];
      Object.entries(this.savedExpressions).forEach(([key, value]) => {
        this.expressions.push({
          key,
          value,
        });
      });
    }

    const tagName = this.config.getConfig(
      'executableExpressionTemplateTag',
      ''
    );
    this.idpTemplateConfig = {
      showPhoto: true,
      allowEmptySearch: true,
      queryNormalSearch: tagName
        ? `/functionexpressiontemplate[tags='${tagName}' and (starts-with(DisplayName,'%SearchText%') or starts-with(Name,'%SearchText%') or ObjectID='%SearchText%')]`
        : "/functionexpressiontemplate[starts-with(DisplayName,'%SearchText%') or starts-with(Name,'%SearchText%') or ObjectID='%SearchText%']",
      queryEmptySearch: tagName
        ? `/functionexpressiontemplate[tags='${tagName}' and starts-with(DisplayName,'%')]`
        : "/functionexpressiontemplate[starts-with(DisplayName,'%')]",
      attributesToShow: [
        {
          field: 'displayname',
          width: 0,
          filterable: true,
          filter: 'text',
          sortable: true,
        },
        {
          field: 'name',
          width: 0,
          filterable: true,
          filter: 'text',
          sortable: true,
        },
        {
          field: 'description',
          width: 0,
          filterable: true,
          filter: 'text',
          sortable: true,
        },
      ],
    };

    this.configWorkflowDataExpression.iterableType = 'array';
    this.configWorkflowDataExpression.saveAs = 'object';
    this.configWorkflowDataExpression.properties = [
      {
        name: 'key',
        displayName: 'key_key',
        required: true,
        type: 'text',
        isKey: true,
        width: 30,
        validation: '^[a-zA-Z0-9_]+$',
        options: [],
      },
      {
        name: 'value',
        displayName: 'key_value',
        required: true,
        type: 'text',
        isKey: false,
        width: 70,
        validationKey: 'workflow/addDelay/workflowDataExpressions',
        options: [],
      },
    ];

    this.configDeltaExpression.iterableType = 'object';
    this.configDeltaExpression.saveAs = 'object';
    this.configDeltaExpression.properties = [
      {
        name: 'key',
        displayName: 'key_key',
        required: true,
        type: 'text',
        isKey: true,
        width: 30,
        validation: '^[a-zA-Z0-9_]+$',
        options: [],
      },
      {
        name: 'value',
        displayName: 'key_value',
        required: true,
        type: 'text',
        isKey: false,
        width: 70,
        options: [],
      },
    ];

    this.configTemplateParameter.iterableType = 'dictionary';
    this.configTemplateParameter.saveAs = 'object';
    this.configTemplateParameter.properties = [
      {
        name: 'key',
        displayName: 'key_name',
        required: true,
        type: 'text',
        isKey: true,
        validation: '^[a-zA-Z_][a-zA-Z0-9_-]*$',
        options: [],
      },
      {
        name: 'displayname',
        displayName: 'key_title',
        required: true,
        type: 'text',
        isKey: false,
        options: [],
      },
      {
        name: 'defaultvalue',
        displayName: 'key_default',
        required: false,
        type: 'text',
        isKey: false,
        isHidden: true,
        options: [],
      },
      {
        name: 'testvalue',
        displayName: 'key_testValue',
        required: false,
        type: 'text',
        isKey: false,
        options: [],
      },
      {
        name: 'datatype',
        displayName: 'l10n_dataType',
        required: false,
        type: 'select',
        isKey: false,
        width: 20,
        default: 'string',
        options: [
          { text: 'String', value: 'string' },
          { text: 'Integer', value: 'integer' },
          { text: 'Boolean', value: 'boolean' },
          { text: 'Reference', value: 'reference' },
          { text: 'Date', value: 'datetime' },
          { text: 'Dictionary', value: 'dictionary' },
        ],
      },
      {
        name: 'description',
        displayName: 'key_description',
        required: false,
        type: 'text',
        isKey: false,
        width: 80,
        options: [],
      },
      {
        name: 'required',
        displayName: 'key_allowNull',
        required: false,
        type: 'boolean',
        isKey: false,
        width: 20,
        default: true,
        reversed: true,
        options: [],
      },
    ];

    this.subscription.add(
      this.clipboard.copyResponse$.subscribe((res: IClipboardResponse) => {
        if (res.isSuccess) {
          this.snackbar.open(this.translate.instant('key_textCopied'), 'OK', {
            duration: 2000,
          });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onExecute() {
    this.isLoading = true;

    this.expressionResult = undefined;
    this.expressionResultRaw = '';
    this.expressionResultString = '';

    this.resultCode = undefined;
    this.resultState = '';

    const expressionBody = this.buildExpressionDef();

    this.subscription.add(
      this.resource
        .executeExpression(expressionBody)
        .pipe(
          tap((result: HttpResponse<string>) => {
            this.resultCode = String(result.status);
            const measuredTime = result.headers.get(
              'x-idabus-measurement-time-ms'
            );
            if (measuredTime !== null && measuredTime !== undefined) {
              this.resultCode = `${this.resultCode} (${measuredTime} ms)`;
            }
            this.resultState = 'success';
            if (result && result.body !== null && result.body !== undefined) {
              this.expressionResultRaw = result.body;
              try {
                const maxDisplaySizeInMB = this.config.getConfig('maxDisplaySizeInMB', 2);
                if (this.utils.objectTooLarge(result.body, maxDisplaySizeInMB)) {
                  this.expressionResult = [];
                  this.expressionResultString = `${this.translate.instant('key_resultTooLargeToDisplay')} (>${maxDisplaySizeInMB}MB)`;
                } else {
                  this.expressionResult = JSON.parse(result.body);
                  this.expressionResultString =
                    typeof this.expressionResult === 'string'
                      ? this.expressionResult
                      : JSON.stringify(this.expressionResult, null, 2);
                }
              } catch {
                this.expressionResult = result.body;
                this.expressionResultString = result.body;
              }
            }
          }),
          catchError((err: HttpErrorResponse) => {
            this.resultCode = String(err.status);
            this.resultState = 'failed';
            this.expressionResultString = this.utils.getServiceError(err);
            return EMPTY;
          }),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe()
    );
  }

  onSaveExpression() {
    let expressionName = '';
    let savingProgress: MatDialogRef<ModalComponent, any>;

    const popupConfig: PopupConfig = new PopupConfig();
    popupConfig.width = 420;
    popupConfig.title = 'l10n_saveExpression';
    popupConfig.style = 'outline';
    popupConfig.data = {
      expressionName: {
        text: 'key_name',
        value: '',
        type: 'text',
        validation: `^[a-zA-Z0-9_.-]*$`,
        required: true,
        focused: true,
      },
    };

    this.subscription.add(
      this.modal
        .popup(popupConfig)
        .pipe(
          switchMap((windowResult: any) => {
            if (!(windowResult instanceof WindowCloseResult)) {
              expressionName = windowResult.expressionName.value;
              if (this.savedExpressions.hasOwnProperty(expressionName)) {
                const confirm = this.modal.show(
                  ModalType.confirm,
                  'key_confirmation',
                  'l10n_expressionExists'
                );
                return confirm.afterClosed();
              } else {
                return of('yes');
              }
            }
            return EMPTY;
          }),
          switchMap((confirmResult: string) => {
            if (confirmResult && confirmResult === 'yes') {
              savingProgress = this.modal.show(
                ModalType.progress,
                'key_savingChanges',
                '',
                '300px'
              );

              if (
                !this.customViewSetting.hasOwnProperty(
                  this.customFunctionExpressionKey
                )
              ) {
                this.customViewSetting[this.customFunctionExpressionKey] = {};
              }
              this.customViewSetting[this.customFunctionExpressionKey][
                expressionName
              ] = {
                expression: this.expression,
              };
              const def = this.buildExpressionDef();
              if (def && Object.keys(def).length > 0) {
                this.customViewSetting[this.customFunctionExpressionKey][
                  expressionName
                ].expressionDef = def;
              }
              this.savedExpressions =
                this.customViewSetting[this.customFunctionExpressionKey];
              this.expressions = [];
              Object.entries(this.savedExpressions).forEach(([key, value]) => {
                this.expressions.push({
                  key,
                  value,
                });
              });
              this.selectedExpression = this.expressions.find(
                (exp) => exp.key === expressionName
              );

              return this.resource.updateCustomSettings();
            }

            return EMPTY;
          }),
          catchError((err: HttpErrorResponse) => {
            this.modal.show(
              ModalType.error,
              'key_error',
              this.utils.getServiceError(err)
            );
            return EMPTY;
          }),
          finalize(() => {
            if (savingProgress) {
              savingProgress.close();
            }
          })
        )
        .subscribe()
    );
  }

  onLoadExpression() {
    if (this.selectedExpression && this.selectedExpression.value) {
      this.expression = this.selectedExpression.value.expression;
      if (this.selectedExpression.value.expressionDef) {
        if (
          this.selectedExpression.value.expressionDef.requestor &&
          this.idpRequestor
        ) {
          this.idpRequestor.value =
            this.selectedExpression.value.expressionDef.requestor;
        }
        if (
          this.selectedExpression.value.expressionDef.target &&
          this.idpTarget
        ) {
          this.idpTarget.value =
            this.selectedExpression.value.expressionDef.target;
        }
        if (
          this.selectedExpression.value.expressionDef.workflowDataExpressions &&
          this.itrWorkflowData
        ) {
          this.itrWorkflowData.value =
            this.selectedExpression.value.expressionDef.workflowDataExpressions;
          this.itrWorkflowData.refresh();
        }
        if (
          this.selectedExpression.value.expressionDef.delta &&
          this.itrDelta
        ) {
          this.itrDelta.value =
            this.selectedExpression.value.expressionDef.delta;
          this.itrDelta.refresh();
        }
        if (
          this.selectedExpression.value.expressionDef.deltaBeforeChange &&
          this.itrDeltaBeforeChange
        ) {
          this.itrDeltaBeforeChange.value =
            this.selectedExpression.value.expressionDef.deltaBeforeChange;
          this.itrDeltaBeforeChange.refresh();
        }
      }
    }
  }

  onDeleteExpression() {
    let savingProgress: MatDialogRef<ModalComponent, any>;

    const confirm = this.modal.show(
      ModalType.confirm,
      'key_confirmation',
      'l10n_deleteExpression'
    );

    confirm
      .afterClosed()
      .pipe(
        switchMap((confirmResult) => {
          if (confirmResult && confirmResult === 'yes') {
            savingProgress = this.modal.show(
              ModalType.progress,
              'key_savingChanges',
              '',
              '300px'
            );

            if (
              this.customViewSetting[
                this.customFunctionExpressionKey
              ].hasOwnProperty(this.selectedExpression.key)
            ) {
              delete this.customViewSetting[this.customFunctionExpressionKey][
                this.selectedExpression.key
              ];
            }
            this.savedExpressions =
              this.customViewSetting[this.customFunctionExpressionKey];
            this.expressions = [];
            Object.entries(this.savedExpressions).forEach(([key, value]) => {
              this.expressions.push({
                key,
                value,
              });
            });
            this.selectedExpression = null;

            return this.resource.updateCustomSettings();
          } else {
            return EMPTY;
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this.modal.show(
            ModalType.error,
            'key_error',
            this.utils.getServiceError(err)
          );
          return EMPTY;
        }),
        finalize(() => {
          if (savingProgress) {
            savingProgress.close();
          }
        })
      )
      .subscribe();
  }

  onRawData() {
    this.resultMode = 'raw';
    this.expressionResultString = this.expressionResultRaw;
  }

  onJsonData() {
    this.resultMode = 'json';
    try {
      this.expressionResultString =
        typeof this.expressionResult === 'string'
          ? this.expressionResult
          : JSON.stringify(this.expressionResult, null, 2);
    } catch { }
  }

  onCopyData() {
    this.clipboard.copy(this.expressionResultString);
  }

  onIdentityDoubleClick(item: BasicResource) {
    this.utils.NavigateToIdentity(item);
  }

  onTemplateChange(template: Array<any>) {
    this.parameterConfig = [];

    if (template && template.length === 1) {
      this.templateId = this.utils.ExtraValue(template[0], 'ObjectID');
    } else {
      this.templateId = '';
    }

    if (this.templateId) {
      this.getTemplateInfo(this.templateId);
    }
  }

  onUpdateTemplateCode() {
    if (this.templateId) {
      let savingProgress: MatDialogRef<ModalComponent, any>;

      const confirm = this.modal.show(
        ModalType.confirm,
        'key_confirmation',
        'l10n_confirmUpdateTemplateCode'
      );
      this.subscription.add(
        confirm
          .afterClosed()
          .pipe(
            switchMap((confirmResult: string) => {
              if (confirmResult && confirmResult === 'yes') {
                savingProgress = this.modal.show(
                  ModalType.progress,
                  'key_savingChanges',
                  '',
                  '300px'
                );
                const resourceToUpdate = {
                  objectid: this.templateId,
                  objecttype: 'functionexpressiontemplate',
                  functionexpression: this.expression,
                };
                return this.resource.updateResource(resourceToUpdate);
              }
              return EMPTY;
            }),
            catchError((err: HttpErrorResponse) => {
              this.modal.show(
                ModalType.error,
                'key_error',
                this.utils.getServiceError(err)
              );
              return EMPTY;
            }),
            finalize(() => {
              if (savingProgress) {
                savingProgress.close();
              }
            })
          )
          .subscribe()
      );
    }
  }

  onSaveAsTemplate() {
    let params = {};
    if (this.templateId) {
      this.parameterConfig.forEach((p) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { paramKey, paramValue, ...rest } = p;
        params[p.paramKey] = rest;
      });
    } else {
      if (this.templateParameter && this.templateParameter.value) {
        params = this.utils.DeepCopy(this.templateParameter.value);
        Object.keys(params).forEach((key) => {
          delete params[key]['testvalue'];
        });
      }
    }
    this.subscription.add(
      this.wizard
        .open('functionexpressiontemplate', {
          expression: this.expression,
          parameters: params,
        })
        .subscribe()
    );
  }

  onReset() {
    const confirm = this.modal.show(
      ModalType.confirm,
      'key_confirmation',
      'key_confirmReset'
    );
    this.subscription.add(
      confirm
        .afterClosed()
        .pipe(
          tap((confirmResult: string) => {
            if (confirmResult && confirmResult === 'yes') {
              this.expression = '';
              this.expressionResult = undefined;
              this.expressionResultRaw = '';
              this.expressionResultString = '';

              this.resultCode = undefined;
              this.resultState = '';

              this.templateId = '';
              this.parameterConfig = [];

              try {
                this.idpRequestor.value = null;
                this.idpTarget.value = null;
                this.idpTemplate.value = null;

                this.itrWorkflowData.value = null;
                this.itrWorkflowData.refresh();

                this.itrDelta.value = null;
                this.itrDelta.refresh();

                this.itrDeltaBeforeChange.value = null;
                this.itrDeltaBeforeChange.refresh();

                this.itrTemplateParameter.value = null;
                this.itrTemplateParameter.refresh();
              } catch { }
            }
          })
        )
        .subscribe()
    );
  }
}
